import { useCallback, useMemo, useState } from 'react'

import { emitEventOnIframe } from '@inno-tools/container-bridge'
import { debounce } from 'es-toolkit'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { SnapArea } from 'shared/types'

import { WINDOW_DRAG_CONSTANTS } from '../constants'
import type {
  SnapRect,
  UseWindowSnapParams,
  UseWindowSnapResult
} from '../types'
import {
  calculateDesiredPositionFromMaximized,
  calculateSnapRect,
  getViewportDimensions,
  isPointInRect,
  isSnapArea,
  restartDragControls
} from '../utils'

export function useWindowDrag({
  iframeRef,
  constraintsRef,
  windowState,
  controls,
  onBeginDrag,
  onCommitPosition,
  setDragging,
  snapEnabled,
  dragEnabled
}: UseWindowSnapParams): UseWindowSnapResult {
  const [animationInProgress, setAnimationInProgress] = useState(false)
  const [dragOrigin, setDragOrigin] = useState<{ x: number; y: number } | null>(
    null
  )

  const updateWindow = useWindowStore(state => state.updateWindow)
  const setWindowMaximized = useWindowStore(state => state.setWindowMaximized)
  const setWindowSnapArea = useWindowStore(state => state.setSnapArea)
  const updateWindowPosition = useWindowStore(
    state => state.updateWindowPosition
  )

  const [snapPreviewRect, setSnapPreviewRect] = useState<SnapRect | null>(null)
  const [snapTarget, setSnapTarget] = useState<SnapArea | null>(null)

  const getTopOffset = useCallback(
    () => constraintsRef.current?.offsetTop || 0,
    [constraintsRef]
  )

  const adjustIfPointerOutside = useCallback(
    (
      clientX: number,
      clientY: number,
      position: { x: number; y: number },
      size: { width: number; height: number }
    ) => {
      const rect = {
        x: position.x,
        y: position.y,
        width: size.width,
        height: size.height
      }
      const point = { x: clientX, y: clientY }

      if (isPointInRect(point, rect)) {
        return position
      }

      return {
        x: clientX - size.width / 2,
        y: WINDOW_DRAG_CONSTANTS.DEFAULT_Y_POSITION
      }
    },
    []
  )

  const detectSnapArea = useCallback(
    (clientX: number, clientY: number): SnapArea | null => {
      const { width: viewportWidth, height: viewportHeight } =
        getViewportDimensions()
      const TOP_OFFSET = getTopOffset()
      const threshold = WINDOW_DRAG_CONSTANTS.EDGE_THRESHOLD

      const nearLeft = clientX <= threshold
      const nearRight = clientX >= viewportWidth - threshold
      const nearTop = clientY <= TOP_OFFSET + threshold
      const nearBottom = clientY >= viewportHeight - threshold

      if (nearLeft) {
        if (nearTop) return SnapArea.TOP_LEFT
        if (nearBottom) return SnapArea.BOTTOM_LEFT
        return SnapArea.LEFT_HALF
      }

      if (nearRight) {
        if (nearTop) return SnapArea.TOP_RIGHT
        if (nearBottom) return SnapArea.BOTTOM_RIGHT
        return SnapArea.RIGHT_HALF
      }

      if (nearTop) {
        return SnapArea.FULLSCREEN
      }

      return null
    },
    [getTopOffset]
  )

  const onCommitPositionDebounced = useMemo(
    () => debounce(onCommitPosition, 10),
    [onCommitPosition]
  )

  const commitSnap = useMemo(
    () =>
      debounce(() => {
        if (!snapTarget) return

        const rect = calculateSnapRect(snapTarget, getTopOffset())
        updateWindowPosition(windowState.id, { x: rect.x, y: rect.y })

        if (snapTarget === SnapArea.FULLSCREEN) {
          if (!windowState.isMaximized) {
            setWindowMaximized(windowState.id, true)
            emitEventOnIframe(
              { type: 'MAXIMIZE_WINDOW', data: { windowId: windowState.id } },
              iframeRef.current as HTMLIFrameElement
            )
          }
        } else if (snapTarget !== null && isSnapArea(snapTarget)) {
          setWindowSnapArea(windowState.id, snapTarget)
          emitEventOnIframe(
            {
              type: 'SNAP_WINDOW',
              data: { windowId: windowState.id, snapArea: snapTarget }
            },
            iframeRef.current as HTMLIFrameElement
          )
        }

        setSnapPreviewRect(null)
        setSnapTarget(null)
      }, 20),
    [
      getTopOffset,
      iframeRef,
      setWindowMaximized,
      setWindowSnapArea,
      snapTarget,
      updateWindowPosition,
      windowState.id,
      windowState.isMaximized
    ]
  )

  const handleUnmaximizeAndRestartDrag = useCallback(
    (
      event: PointerEvent,
      clientX: number,
      clientY: number,
      topOffset: number
    ) => {
      const { width: viewportWidth, height: viewportHeight } =
        getViewportDimensions()

      // Calculate cursor position as percentage of viewport
      const originX = clientX / viewportWidth
      const originY = (clientY - topOffset) / (viewportHeight - topOffset)
      setDragOrigin({ x: originX, y: originY })

      const desiredPosition = calculateDesiredPositionFromMaximized(
        clientX,
        clientY,
        windowState.size,
        topOffset
      )
      const adjusted = adjustIfPointerOutside(
        clientX,
        clientY,
        desiredPosition,
        windowState.size
      )

      updateWindow(windowState.id, {
        position: adjusted,
        isMaximized: false
      })

      restartDragControls(
        controls,
        event,
        WINDOW_DRAG_CONSTANTS.DRAG_RESTART_DELAY_MAXIMIZED
      )
    },
    [
      adjustIfPointerOutside,
      controls,
      updateWindow,
      windowState.id,
      windowState.size
    ]
  )

  const handleUnsnapAndRestartDrag = useCallback(
    (
      event: PointerEvent,
      clientX: number,
      clientY: number,
      snapArea: SnapArea
    ) => {
      const rect = calculateSnapRect(snapArea, getTopOffset())
      const adjusted = adjustIfPointerOutside(
        clientX,
        clientY,
        { x: rect.x, y: rect.y },
        windowState.size
      )

      updateWindow(windowState.id, {
        position: adjusted,
        snapArea: null
      })

      restartDragControls(
        controls,
        event,
        WINDOW_DRAG_CONSTANTS.DRAG_RESTART_DELAY_SNAPPED
      )
    },
    [
      adjustIfPointerOutside,
      getTopOffset,
      controls,
      updateWindow,
      windowState.id,
      windowState.size
    ]
  )

  const handleMotionDragStart = useCallback(
    (ev: MouseEvent | TouchEvent | PointerEvent, _info?: unknown) => {
      onBeginDrag()

      if (!dragEnabled) return

      const event = ev as PointerEvent
      const { clientX, clientY } = event
      const topOffset = getTopOffset()

      if (windowState.isMaximized) {
        handleUnmaximizeAndRestartDrag(event, clientX, clientY, topOffset)
        return
      }

      if (windowState.snapArea) {
        handleUnsnapAndRestartDrag(
          event,
          clientX,
          clientY,
          windowState.snapArea
        )
      }
    },
    [
      dragEnabled,
      getTopOffset,
      handleUnmaximizeAndRestartDrag,
      handleUnsnapAndRestartDrag,
      onBeginDrag,
      windowState.isMaximized,
      windowState.snapArea
    ]
  )

  const handleMotionDrag = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, _info?: unknown) => {
      const { clientX, clientY } = event as PointerEvent
      const detectedArea = detectSnapArea(clientX, clientY)

      setSnapTarget(detectedArea)

      if (detectedArea && snapEnabled) {
        setSnapPreviewRect(calculateSnapRect(detectedArea, getTopOffset()))
      } else {
        setSnapPreviewRect(null)
      }
    },
    [detectSnapArea, getTopOffset, snapEnabled]
  )

  const handleMotionDragEnd = useCallback(() => {
    setAnimationInProgress(true)

    if (snapTarget && snapEnabled) {
      onCommitPositionDebounced({ clamp: false })
      commitSnap()
    } else {
      onCommitPositionDebounced({ clamp: true })
    }

    // this is just as a fallback if the motion transition end does not trigger
    setTimeout(() => {
      setAnimationInProgress(false)
    }, 300)
  }, [snapTarget, snapEnabled, commitSnap, onCommitPositionDebounced])

  const handleMotionTransitionEnd = useCallback(() => {
    setAnimationInProgress(false)
    setDragOrigin(null)
  }, [])

  const getSnapRect = useCallback(
    (area: SnapArea): SnapRect => calculateSnapRect(area, getTopOffset()),
    [getTopOffset]
  )

  const clearSnap = useCallback(() => {
    setSnapPreviewRect(null)
    setSnapTarget(null)
  }, [])

  const handleTitleBarMouseDown = useCallback(
    (e: React.PointerEvent) => {
      if (!dragEnabled || animationInProgress) return

      controls.cancel()
      controls.start(e, {
        distanceThreshold: WINDOW_DRAG_CONSTANTS.DRAG_DISTANCE_THRESHOLD
      })

      setDragging(true)

      requestAnimationFrame(() => {
        const handlePointerUp = () => {
          controls.stop()
          controls.cancel()
          globalThis.window.removeEventListener('pointerup', handlePointerUp)
          setDragging(false)
          clearSnap()
        }

        globalThis.window.addEventListener('pointerup', handlePointerUp)
      })
    },
    [dragEnabled, controls, setDragging, clearSnap, animationInProgress]
  )

  return {
    animationInProgress,
    snapPreviewRect,
    dragOrigin,
    handleMotionDragStart,
    handleMotionDrag,
    handleMotionDragEnd,
    handleMotionTransitionEnd,
    handleTitleBarMouseDown,
    getSnapRect,
    clearSnap
  }
}
